import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';

import {
  BASIC_CATEGORY_INDEX,
  CELL_SX,
  EXTRA_NIGHT_DIVIDER_SX,
  GROUP_DIVIDER_SX,
  HEADER_SX,
  PACKAGE_CATEGORIES,
  groupStartSx,
  stickyCellSx,
} from './packageCategories';
import { ASIA_ONLY_LOS, hasSellPrice, roundNetInput, takenRoomAssetKeys } from './packageOptionsModel';
import {
  type CategoryRoom,
  type PackageOptionId,
  type RoomAssetLink,
  type RoomPricing,
  roomDisplayName,
} from './types';

const NO_SPINNER_SX = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

const displayPrice = (value: string | undefined) => {
  if (!value) {
    return '';
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(Math.round(parsed)) : value;
};

const COL_WIDTH = 110;
const SINGLE_LOS_COL_WIDTH = 190;
const ROOM_TYPE_LABEL_COL_WIDTH = 60;
const ROOM_TYPE_NAME_COL_WIDTH = 220;
const ASIA_ONLY_COL_WIDTH = 100;

type PricingTableProps = {
  assets: Array<RoomAssetLink>;
  netCurrency: string;
  onDeleteRoomType: (categoryIndex: number, id: PackageOptionId) => void;
  onSelectAsset: (categoryIndex: number, id: PackageOptionId, asset: RoomAssetLink) => void;
  onToggleAsiaOnly: (categoryIndex: number, id: PackageOptionId) => void;
  onUpdateRoomPricing: (id: PackageOptionId, defaultOccupancy: string, changes: Partial<RoomPricing>) => void;
  onSetRoomSellPrice: (id: PackageOptionId, defaultOccupancy: string, nights: number, value: string) => void;
  onSetRoomNetRate: (id: PackageOptionId, defaultOccupancy: string, nights: number, value: string) => void;
  pricingByRoomId: Record<PackageOptionId, RoomPricing>;
  roomsByCategory: Array<Array<CategoryRoom>>;
  sortedLos: Array<number>;
};

export default function PricingTable({
  assets,
  netCurrency,
  onDeleteRoomType,
  onSelectAsset,
  onToggleAsiaOnly,
  onUpdateRoomPricing,
  onSetRoomSellPrice,
  onSetRoomNetRate,
  pricingByRoomId,
  roomsByCategory,
  sortedLos,
}: PricingTableProps) {
  const totalRoomCount = roomsByCategory.reduce((total, rooms) => total + rooms.length, 0);
  const basicRooms = roomsByCategory[BASIC_CATEGORY_INDEX];

  // Allocation is set once on BASIC and mirrored read-only across categories. An unlinked
  // row has no BASIC room to mirror, matching MarginTable's base lookup.
  function allocationForAsset(assetId: string) {
    if (!assetId) {
      return '';
    }
    const basicRoom = basicRooms.find((room) => room.assetId === assetId);
    return basicRoom === undefined ? '' : (pricingByRoomId[basicRoom.id]?.allocation ?? '');
  }

  const priceColWidth = sortedLos.length === 1 ? SINGLE_LOS_COL_WIDTH : COL_WIDTH;
  const colWidths = [
    ROOM_TYPE_LABEL_COL_WIDTH,
    ROOM_TYPE_NAME_COL_WIDTH,
    ASIA_ONLY_COL_WIDTH,
    COL_WIDTH,
    COL_WIDTH,
    ...sortedLos.flatMap(() => [priceColWidth, priceColWidth]),
    COL_WIDTH,
    COL_WIDTH,
  ];

  return (
    <TableContainer sx={{ mt: 3, maxWidth: '100%', overflowX: 'auto', width: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: 'max-content' }}>
        <colgroup>
          {colWidths.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <TableBody>
          <TableRow>
            <TableCell colSpan={2} sx={{ ...stickyCellSx(0) }} />
            <TableCell align="center" colSpan={3} sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX }}>
              Package
            </TableCell>
            <TableCell
              align="center"
              colSpan={sortedLos.length}
              sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX, whiteSpace: 'nowrap' }}
            >
              Sell price (AUD)
            </TableCell>
            <TableCell
              align="center"
              colSpan={sortedLos.length}
              sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX, whiteSpace: 'nowrap' }}
            >
              Net rate ({netCurrency})
            </TableCell>
            <TableCell align="center" colSpan={2} sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, color: orange[700] }}>
              Extra night
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2} sx={{ ...HEADER_SX, ...stickyCellSx(0) }}>
              Room type
            </TableCell>
            <TableCell align="center" sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX, whiteSpace: 'nowrap' }}>
              Asia only
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              Allocation
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              Occupancy
            </TableCell>
            {sortedLos.map((nights, index) => (
              <TableCell align="center" key={`sell-${nights}`} sx={{ ...HEADER_SX, ...groupStartSx(index) }}>
                {nights}N
              </TableCell>
            ))}
            {sortedLos.map((nights, index) => (
              <TableCell align="center" key={`net-${nights}`} sx={{ ...HEADER_SX, ...groupStartSx(index) }}>
                {nights}N
              </TableCell>
            ))}
            <TableCell align="center" sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX }}>
              Sell (AUD)
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              Net ({netCurrency})
            </TableCell>
          </TableRow>
          {PACKAGE_CATEGORIES.map((category, categoryIndex) => {
            const rooms = roomsByCategory[categoryIndex];
            if (categoryIndex !== BASIC_CATEGORY_INDEX && rooms.every((room) => !room.assetName)) {
              return null;
            }
            const rowCellSx = { ...CELL_SX, bgcolor: category.bgColor };
            const labelSx = {
              ...stickyCellSx(0, category.bgColor),
              color: category.labelColor,
              transform: 'rotate(180deg)',
              writingMode: 'vertical-lr' as const,
            };
            const { defaultOccupancy } = category;

            return rooms.map(({ id, assetId, assetName, asiaOnly, suffix }, index) => {
              const pricing = pricingByRoomId[id];
              const isBaseRoom = categoryIndex === BASIC_CATEGORY_INDEX;
              const takenAssetKeys = takenRoomAssetKeys(basicRooms, id);

              return (
                <TableRow key={id}>
                  {index === 0 && (
                    <TableCell align="center" rowSpan={rooms.length} sx={labelSx}>
                      {category.label}
                    </TableCell>
                  )}
                  <TableCell sx={{ ...stickyCellSx(ROOM_TYPE_LABEL_COL_WIDTH, category.bgColor) }}>
                    {isBaseRoom ? (
                      <Select
                        displayEmpty
                        error={!assetName}
                        fullWidth
                        onChange={(e) => {
                          const asset = assets.find((candidate) => candidate.id === e.target.value);
                          if (asset && !takenAssetKeys.has(asset.id)) {
                            onSelectAsset(categoryIndex, id, asset);
                          }
                        }}
                        renderValue={(value) => (value ? assetName : 'Select asset...')}
                        size="small"
                        value={assetId}
                      >
                        {assets.map((asset) => {
                          const isTaken = takenAssetKeys.has(asset.id);

                          return (
                            <MenuItem disabled={isTaken} key={asset.id} value={asset.id}>
                              {isTaken ? `${asset.name} (already used)` : asset.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : (
                      <Typography variant="body2">{roomDisplayName({ assetName, suffix })}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ ...rowCellSx, ...GROUP_DIVIDER_SX }}>
                    {isBaseRoom && (
                      <Checkbox
                        checked={asiaOnly}
                        disabled={!assetName}
                        inputProps={{ 'aria-label': `Asia only ${assetName}` }}
                        onChange={() => onToggleAsiaOnly(categoryIndex, id)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={rowCellSx}>
                    {(() => {
                      const allocation = isBaseRoom ? (pricing?.allocation ?? '') : allocationForAsset(assetId);
                      const isNumeric = /^\d*$/.test(allocation);
                      return (
                        <TextField
                          disabled={!isBaseRoom}
                          fullWidth={isNumeric}
                          required={isBaseRoom && hasSellPrice(pricing)}
                          onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { allocation: e.target.value })}
                          placeholder="e.g. 5"
                          size="small"
                          sx={isNumeric ? undefined : { width: 250 }}
                          value={allocation}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={rowCellSx}>
                    <TextField
                      fullWidth
                      onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { occupancy: e.target.value })}
                      placeholder="e.g. 2A"
                      size="small"
                      value={pricing?.occupancy ?? defaultOccupancy}
                    />
                  </TableCell>
                  {sortedLos.map((nights, losIndex) => (
                    <TableCell key={`sell-input-${nights}`} sx={{ ...rowCellSx, ...groupStartSx(losIndex) }}>
                      {asiaOnly && nights !== ASIA_ONLY_LOS ? (
                        <TextField disabled fullWidth size="small" value="-" />
                      ) : (
                        <TextField
                          fullWidth
                          inputProps={{ min: 0 }}
                          onChange={(e) => onSetRoomSellPrice(id, defaultOccupancy, nights, e.target.value)}
                          size="small"
                          sx={NO_SPINNER_SX}
                          type="number"
                          value={displayPrice(pricing?.sellByLos[nights])}
                        />
                      )}
                    </TableCell>
                  ))}
                  {sortedLos.map((nights, losIndex) => (
                    <TableCell key={`net-input-${nights}`} sx={{ ...rowCellSx, ...groupStartSx(losIndex) }}>
                      {asiaOnly && nights !== ASIA_ONLY_LOS ? (
                        <TextField disabled fullWidth size="small" value="-" />
                      ) : (
                        <TextField
                          fullWidth
                          inputProps={{ min: 0 }}
                          onChange={(e) => onSetRoomNetRate(id, defaultOccupancy, nights, e.target.value)}
                          size="small"
                          sx={NO_SPINNER_SX}
                          type="number"
                          value={displayPrice(pricing?.netByLos[nights])}
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...rowCellSx, ...EXTRA_NIGHT_DIVIDER_SX }}>
                    <TextField
                      fullWidth
                      inputProps={{ min: 0 }}
                      onChange={(e) =>
                        onUpdateRoomPricing(id, defaultOccupancy, {
                          extraNightSell: e.target.value,
                          extraNightSellEdited: e.target.value.trim() !== '',
                        })
                      }
                      size="small"
                      sx={NO_SPINNER_SX}
                      type="number"
                      value={displayPrice(pricing?.extraNightSell)}
                    />
                  </TableCell>
                  <TableCell sx={{ ...rowCellSx, position: 'relative' }}>
                    <TextField
                      fullWidth
                      inputProps={{ min: 0 }}
                      onChange={(e) =>
                        onUpdateRoomPricing(id, defaultOccupancy, { extraNightNet: roundNetInput(e.target.value) })
                      }
                      size="small"
                      sx={NO_SPINNER_SX}
                      type="number"
                      value={displayPrice(pricing?.extraNightNet)}
                    />
                    {totalRoomCount > 1 && (
                      <IconButton
                        onClick={() => onDeleteRoomType(categoryIndex, id)}
                        size="small"
                        sx={{ position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)' }}
                      >
                        <CloseIcon color="error" fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            });
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
